import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  IoCloseOutline,
  IoTrashOutline,
  IoCheckmarkOutline,
  IoCreateOutline,
  IoPersonOutline,
  IoBulbOutline,
  IoTimeOutline,
  IoBusinessOutline,
  IoDownloadOutline,
} from 'react-icons/io5';
import {
  addCompanyMemory,
  addUserMemory,
  deleteCompanyMemory,
  deleteUserMemory,
  downloadSavedFile,
  fetchBuilderProfile,
  fetchCompanyActivity,
  fetchCompanyProfile,
  getIdToken,
  updateCompanyDescription,
  updateCompanyMemory,
  updateUserMemory,
} from './atlas/atlasApi';
import {
  StructuredMemorySections,
  getProfileCopy,
  isStructuredProfile,
  mergeUpdatedMemory,
  normalizeMemory,
} from './BuilderProfileMemories';
import './BuilderProfileModal.css';

const ATLAS_AGENT_API_URL = process.env.REACT_APP_ATLAS_AGENT_API_URL;

const EMPTY_DRAFTS = {
  work: { text: '', type: 'preference' },
  people: { text: '', type: 'relationship' },
  identity: { text: '', type: 'identity' },
};

function formatDate(isoString) {
  if (!isoString) return '';
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatDateTime(isoString) {
  if (!isoString) return '';
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatActivityType(actionType) {
  return String(actionType || 'activity')
    .replace(/^company_/, '')
    .replaceAll('_', ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function getActivityTypeClass(actionType) {
  const type = String(actionType || '').toLowerCase();
  if (type.includes('conversation')) return 'conversation';
  if (type.includes('file')) return 'file';
  if (type.includes('memory')) return 'memory';
  if (type.includes('description')) return 'description';
  return 'default';
}

function getActivityFileIds(item) {
  const ids = [];
  if (item?.file_id) ids.push(String(item.file_id));
  const metadataIds = Array.isArray(item?.metadata?.file_ids) ? item.metadata.file_ids : [];
  metadataIds.forEach((fileId) => {
    if (fileId) ids.push(String(fileId));
  });
  return Array.from(new Set(ids));
}

function getActivityFiles(item) {
  const names = Array.isArray(item?.file_names) ? item.file_names.filter(Boolean) : [];
  const ids = getActivityFileIds(item);
  return names.map((name, index) => ({
    filename: String(name),
    file_id: ids[index] || (names.length === 1 ? ids[0] : null),
  }));
}

function cleanActivitySummary(item) {
  const summary = String(item?.summary || 'Company activity recorded.').trim();
  const fileCount = Array.isArray(item?.file_names) ? item.file_names.length : 0;
  const memory = String(item?.metadata?.memory || '').trim();
  if (memory && item?.action_type === 'company_memory_create') {
    return `Company memory saved: ${memory}`;
  }
  if (memory && item?.action_type === 'company_memory_update') {
    return `Company memory updated: ${memory}`;
  }
  if (!fileCount) return summary;

  if (item?.action_type === 'company_file_upload') {
    return fileCount === 1
      ? 'File dropped into company workspace.'
      : `${fileCount} files dropped into company workspace.`;
  }
  if (item?.action_type === 'company_file_revision') {
    return 'File saved to company workspace.';
  }
  return summary;
}

export default function BuilderProfileModal({ isOpen, onClose, user, selectedClient, clients, isAdmin, workspaceContext, initialTab = 'profile', embedded = false }) {
  // Determine which user's profile to show
  // If admin has a different client selected, show that client's profile
  const viewingUserId = isAdmin && selectedClient && String(selectedClient) !== String(user?.id)
    ? String(selectedClient)
    : null;
  const viewingClient = viewingUserId && clients
    ? clients.find(c => String(c.id) === viewingUserId)
    : null;
  const displayName = viewingClient
    ? (viewingClient.name || viewingClient.email || `User ${viewingUserId}`)
    : (user?.name || user?.email || 'Your profile');
  const isCompanyScope = Boolean(workspaceContext?.isCompanyScope && workspaceContext?.companyId);
  const companyId = workspaceContext?.companyId;
  const companyName = workspaceContext?.companyName || 'Company';
  const modalTitle = isCompanyScope ? 'Shared Company Information' : 'User Profile';
  const modalSubtitle = isCompanyScope ? companyName : displayName;
  const [description, setDescription] = useState('');
  const [savedDescription, setSavedDescription] = useState('');
  const [memories, setMemories] = useState([]);
  const [profileSummary, setProfileSummary] = useState(null);
  const [structured, setStructured] = useState(false);
  const [drafts, setDrafts] = useState(EMPTY_DRAFTS);
  const [memorySaving, setMemorySaving] = useState(false);
  const [activityItems, setActivityItems] = useState([]);
  const [activityCursor, setActivityCursor] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [newMemory, setNewMemory] = useState('');
  const [editingMemory, setEditingMemory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  const [descriptionSaved, setDescriptionSaved] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [downloadingActivityFileId, setDownloadingActivityFileId] = useState(null);
  const [error, setError] = useState(null);
  const textareaRef = useRef(null);
  const modalRef = useRef(null);

  // Fetch profile data
  const fetchProfile = useCallback(async () => {
    if (!ATLAS_AGENT_API_URL) return;
    setLoading(true);
    setError(null);
    try {
      const data = isCompanyScope
        ? await fetchCompanyProfile({ companyId })
        : await fetchBuilderProfile({ ownerUserId: viewingUserId });
      setDescription(data.description || '');
      setSavedDescription(data.description || '');
      setMemories((data.memories || []).map(normalizeMemory));
      // Typed memories mean the new grouped layout; an old backend keeps the flat list.
      setStructured(isStructuredProfile(data));
      setProfileSummary(data.profile && typeof data.profile === 'object' ? data.profile : null);
    } catch (err) {
      setError(err.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [companyId, isCompanyScope, viewingUserId]);

  const fetchActivity = useCallback(async ({ append = false, beforeActivityId = null } = {}) => {
    if (!isCompanyScope || !companyId) return;
    setActivityLoading(true);
    setError(null);
    try {
      const data = await fetchCompanyActivity({ companyId, limit: 200, beforeActivityId });
      const items = data.items || [];
      setActivityItems((prev) => append ? [...prev, ...items] : items);
      setActivityCursor(data.next_before_activity_id || null);
    } catch (err) {
      setError(err.message || 'Failed to load work history');
    } finally {
      setActivityLoading(false);
    }
  }, [companyId, isCompanyScope]);

  // Re-fetch whenever modal opens or the viewed user changes
  // Using viewingUserId directly (not fetchProfile) to avoid stale closure issues
  useEffect(() => {
    if (isOpen) {
      setEditingDescription(false);
      setDescriptionSaved(false);
      setActiveTab(isCompanyScope && initialTab === 'audit' ? 'audit' : 'profile');
      setActivityItems([]);
      setActivityCursor(null);
      setEditingMemory(null);
      setDrafts(EMPTY_DRAFTS);
      fetchProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, viewingUserId, initialTab, isCompanyScope]);

  // Focus textarea when editing
  useEffect(() => {
    if (editingDescription && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(
        textareaRef.current.value.length,
        textareaRef.current.value.length
      );
    }
  }, [editingDescription]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Close on backdrop click
  const handleBackdropClick = useCallback(
    (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    },
    [onClose]
  );

  // Save description
  const handleSaveDescription = useCallback(async () => {
    if (!ATLAS_AGENT_API_URL) return;
    setSaving(true);
    try {
      const idToken = await getIdToken();
      if (isCompanyScope) {
        await updateCompanyDescription({ companyId, description: description.trim() });
        setSavedDescription(description.trim());
        setEditingDescription(false);
        setDescriptionSaved(true);
        fetchActivity();
        setTimeout(() => setDescriptionSaved(false), 2000);
        return;
      }
      const url = new URL(`${ATLAS_AGENT_API_URL}/v1/builder-description`);
      if (viewingUserId) url.searchParams.set('owner_user_id', viewingUserId);
      const res = await fetch(url.toString(), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ description: description.trim() }),
      });
      if (!res.ok) throw new Error('Failed to save description');
      setSavedDescription(description.trim());
      setEditingDescription(false);
      setDescriptionSaved(true);
      setTimeout(() => setDescriptionSaved(false), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }, [companyId, description, fetchActivity, isCompanyScope, viewingUserId]);

  const handleDownloadActivityFile = useCallback(async (file) => {
    if (!file?.file_id) return;
    setDownloadingActivityFileId(file.file_id);
    try {
      await downloadSavedFile({
        fileId: file.file_id,
        filename: file.filename,
        workspaceContext: {
          ...workspaceContext,
          scopeType: 'company',
          mode: 'company',
          isCompanyScope: true,
          companyId,
        },
      });
    } catch (err) {
      setError(err.message || 'Failed to download file');
    } finally {
      setDownloadingActivityFileId(null);
    }
  }, [companyId, workspaceContext]);

  const handleAddMemory = useCallback(async () => {
    const trimmed = newMemory.trim();
    if (!trimmed || !isCompanyScope) return;
    setSaving(true);
    try {
      const result = await addCompanyMemory({ companyId, memory: trimmed });
      const saved = result?.memory && typeof result.memory === 'object' ? normalizeMemory(result.memory) : null;
      setMemories((prev) => [saved || {
        id: result.memory_id,
        memory_id: result.memory_id,
        memory: trimmed,
        created_by: 'user',
        created_at: new Date().toISOString(),
      }, ...prev]);
      setNewMemory('');
      fetchActivity();
    } catch (err) {
      setError(err.message || 'Failed to add memory');
    } finally {
      setSaving(false);
    }
  }, [companyId, fetchActivity, isCompanyScope, newMemory]);

  const handleChangeDraft = useCallback((key, patch) => {
    setDrafts((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  }, []);

  // Personal add from one of the typed sections (work / people / identity).
  const handleAddTypedMemory = useCallback(async (key) => {
    const draft = drafts[key];
    const trimmed = draft?.text.trim();
    if (!trimmed || isCompanyScope) return;
    setMemorySaving(true);
    try {
      const result = await addUserMemory({ memory: trimmed, type: draft.type, ownerUserId: viewingUserId });
      const saved = result?.memory && typeof result.memory === 'object' ? normalizeMemory(result.memory) : null;
      if (saved?.id != null) {
        setMemories((prev) => [{ type: draft.type, created_by: 'user', ...saved }, ...prev]);
      } else {
        fetchProfile();
      }
      setDrafts((prev) => ({ ...prev, [key]: { ...prev[key], text: '' } }));
    } catch (err) {
      setError(err.message || 'Failed to add memory');
    } finally {
      setMemorySaving(false);
    }
  }, [drafts, fetchProfile, isCompanyScope, viewingUserId]);

  const handleStartEdit = useCallback((memory) => {
    setEditingMemory({
      id: memory.id,
      memory: memory.memory || '',
      detail: memory.detail || '',
      hadDetail: Boolean(memory.detail),
    });
  }, []);

  const handleChangeEdit = useCallback((patch) => {
    setEditingMemory((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  // Personal edit. The backend may supersede the row, so the response can carry a new id.
  const handleSaveUserMemoryEdit = useCallback(async () => {
    if (!editingMemory || isCompanyScope) return;
    const trimmed = editingMemory.memory.trim();
    if (!trimmed) return;
    const detail = String(editingMemory.detail || '').trim();
    setMemorySaving(true);
    try {
      const result = await updateUserMemory({
        memoryId: editingMemory.id,
        memory: trimmed,
        // Send detail only when there was one or the user typed one; an emptied detail clears it.
        detail: editingMemory.hadDetail || detail ? (detail || null) : undefined,
        ownerUserId: viewingUserId,
      });
      const updated = result?.memory && typeof result.memory === 'object' ? result.memory : null;
      setMemories((prev) => prev.map((memory) => (
        String(memory.id) === String(editingMemory.id)
          ? mergeUpdatedMemory(memory, updated || { memory: trimmed, detail: detail || null }, trimmed)
          : memory
      )));
      setEditingMemory(null);
    } catch (err) {
      setError(err.message || 'Failed to update memory');
    } finally {
      setMemorySaving(false);
    }
  }, [editingMemory, isCompanyScope, viewingUserId]);

  const handleSaveMemoryEdit = useCallback(async () => {
    if (!editingMemory || !isCompanyScope) return;
    const trimmed = editingMemory.memory.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      const result = await updateCompanyMemory({ companyId, memoryId: editingMemory.id, memory: trimmed });
      const updated = result?.memory && typeof result.memory === 'object' ? result.memory : null;
      setMemories((prev) => prev.map((memory) => (
        String(memory.id) === String(editingMemory.id)
          ? mergeUpdatedMemory(memory, updated, trimmed)
          : memory
      )));
      setEditingMemory(null);
      fetchActivity();
    } catch (err) {
      setError(err.message || 'Failed to update memory');
    } finally {
      setSaving(false);
    }
  }, [companyId, editingMemory, fetchActivity, isCompanyScope]);

  // Delete memory
  const handleDeleteMemory = useCallback(async (memoryId) => {
    if (!ATLAS_AGENT_API_URL) return;
    setDeletingId(memoryId);
    try {
      if (isCompanyScope) {
        await deleteCompanyMemory({ companyId, memoryId });
        setMemories((prev) => prev.filter((m) => String(m.id) !== String(memoryId)));
        fetchActivity();
        return;
      }
      await deleteUserMemory({ memoryId, ownerUserId: viewingUserId });
      setMemories((prev) => prev.filter((m) => String(m.id) !== String(memoryId)));
    } catch (err) {
      setError(err.message);
    } finally {
      setDeletingId(null);
    }
  }, [companyId, fetchActivity, isCompanyScope, viewingUserId]);

  useEffect(() => {
    if (isOpen && isCompanyScope && activeTab === 'audit' && activityItems.length === 0 && !activityLoading) {
      fetchActivity();
    }
  }, [activeTab, activityItems.length, activityLoading, fetchActivity, isCompanyScope, isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className={embedded ? 'builder-profile-embedded' : 'builder-profile-backdrop'}
      onClick={embedded ? undefined : handleBackdropClick}
    >
      <div
        className={`builder-profile-modal ${embedded ? 'builder-profile-modal-embedded' : ''} ${isCompanyScope && activeTab === 'audit' ? 'builder-profile-modal-wide' : ''}`}
        ref={modalRef}
      >
        {/* Header */}
        <div className="builder-profile-header">
          <div className="builder-profile-header-left">
            <div className="builder-profile-header-icon">
              {isCompanyScope ? <IoBusinessOutline size={20} /> : <IoPersonOutline size={20} />}
            </div>
            <div>
              <h2 className="builder-profile-title">{modalTitle}</h2>
              <p className="builder-profile-subtitle">
                {modalSubtitle}
                {!isCompanyScope && viewingClient && <span style={{ opacity: 0.7 }}> (client)</span>}
              </p>
            </div>
          </div>
          {!embedded && (
            <button
              type="button"
              className="builder-profile-close"
              onClick={onClose}
              aria-label="Close"
            >
              <IoCloseOutline size={22} />
            </button>
          )}
        </div>

        {isCompanyScope && (
          <div className="builder-profile-tabs">
            <button
              type="button"
              className={`builder-profile-tab ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={() => setActiveTab('profile')}
            >
              Info
            </button>
            <button
              type="button"
              className={`builder-profile-tab ${activeTab === 'audit' ? 'active' : ''}`}
              onClick={() => setActiveTab('audit')}
            >
              Work History
            </button>
          </div>
        )}

        {/* Content */}
        <div className="builder-profile-content">
          {loading ? (
            <div className="builder-profile-loading">
              <span className="builder-profile-spinner" />
              <span>Loading profile…</span>
            </div>
          ) : (
            <>
              {error && (
                <div className="builder-profile-error">
                  <span>{error}</span>
                  <button type="button" onClick={() => setError(null)}>
                    <IoCloseOutline size={14} />
                  </button>
                </div>
              )}

              {isCompanyScope && activeTab === 'audit' ? (
                <div className="builder-profile-section">
                  <div className="builder-profile-section-header">
                    <div className="builder-profile-section-label">
                      <IoTimeOutline size={16} />
                      <span>Company Work History</span>
                    </div>
                    <span className="builder-profile-memory-count">{activityItems.length}</span>
                  </div>
                  {activityLoading && activityItems.length === 0 ? (
                    <div className="builder-profile-loading">
                      <span className="builder-profile-spinner" />
                      <span>Loading work history...</span>
                    </div>
                  ) : activityItems.length === 0 ? (
                    <div className="builder-profile-empty">
                      <span>No company activity yet.</span>
                    </div>
                  ) : (
                    <div className="builder-profile-activity-table-wrap">
                      <table className="builder-profile-activity-table">
                        <thead>
                          <tr>
                            <th>Date / Time</th>
                            <th>Type of Action</th>
                            <th>Description</th>
                          </tr>
                        </thead>
                        <tbody>
                          {activityItems.map((item) => {
                            const files = getActivityFiles(item);
                            return (
                              <tr key={item.activity_id}>
                                <td className="builder-profile-activity-date">
                                  {formatDateTime(item.updated_at || item.created_at)}
                                </td>
                                <td>
                                  <span className={`builder-profile-activity-type ${getActivityTypeClass(item.action_type)}`}>
                                    {formatActivityType(item.action_type)}
                                  </span>
                                </td>
                                <td className="builder-profile-activity-description">
                                  <div>{cleanActivitySummary(item)}</div>
                                  {files.length > 0 ? (
                                    <div className="builder-profile-activity-files">
                                      {files.map((file, index) => (
                                        file.file_id ? (
                                          <button
                                            key={`${file.file_id}-${index}`}
                                            type="button"
                                            className="builder-profile-activity-file-chip downloadable"
                                            onClick={() => handleDownloadActivityFile(file)}
                                            disabled={downloadingActivityFileId === file.file_id}
                                            title={`Download ${file.filename}`}
                                          >
                                            <span>{file.filename}</span>
                                            <IoDownloadOutline size={14} />
                                          </button>
                                        ) : (
                                          <span
                                            key={`${file.filename}-${index}`}
                                            className="builder-profile-activity-file-chip"
                                          >
                                            {file.filename}
                                          </span>
                                        )
                                      ))}
                                    </div>
                                  ) : null}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      {activityCursor && (
                        <button
                          type="button"
                          className="builder-profile-btn builder-profile-btn-ghost builder-profile-load-more"
                          onClick={() => fetchActivity({ append: true, beforeActivityId: activityCursor })}
                          disabled={activityLoading}
                        >
                          {activityLoading ? 'Loading...' : 'Load more'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <>

              {/* Description Section */}
              <div className="builder-profile-section">
                <div className="builder-profile-section-header">
                  <h3 className="builder-profile-section-label">
                    <IoCreateOutline size={16} aria-hidden="true" />
                    <span>
                      {structured
                        ? getProfileCopy(isCompanyScope).descriptionTitle
                        : (isCompanyScope ? 'Company Description' : 'User Description')}
                    </span>
                  </h3>
                  {!editingDescription && (
                    <button
                      type="button"
                      className="builder-profile-edit-btn"
                      onClick={() => setEditingDescription(true)}
                    >
                      {savedDescription ? 'Edit' : 'Add'}
                    </button>
                  )}
                </div>
                <p className="builder-profile-section-hint">
                  {isCompanyScope
                    ? 'Shared company information is available to Atlas in company mode and edits are recorded in company work history.'
                    : 'Tell Atlas about your company—what you build, where you operate, your target market. This helps Atlas tailor every conversation.'}
                </p>
                {editingDescription ? (
                  <div className="builder-profile-description-edit">
                    <textarea
                      ref={textareaRef}
                      className="builder-profile-textarea"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="e.g., We're a semi-custom builder in the DFW metroplex focused on first-time move-up buyers. Price range $350K–$550K. Currently active in 3 communities with 2 more in land acquisition."
                      maxLength={2000}
                      rows={4}
                    />
                    <div className="builder-profile-description-actions">
                      <span className="builder-profile-char-count">
                        {description.length}/2000
                      </span>
                      <div className="builder-profile-description-btns">
                        <button
                          type="button"
                          className="builder-profile-btn builder-profile-btn-ghost"
                          onClick={() => {
                            setDescription(savedDescription);
                            setEditingDescription(false);
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="builder-profile-btn builder-profile-btn-primary"
                          onClick={handleSaveDescription}
                          disabled={saving}
                        >
                          {saving ? 'Saving…' : 'Save'}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : savedDescription ? (
                  <div className="builder-profile-description-display">
                    <p>{savedDescription}</p>
                    {descriptionSaved && (
                      <span className="builder-profile-saved-badge">
                        <IoCheckmarkOutline size={12} /> Saved
                      </span>
                    )}
                  </div>
                ) : (
                  <div className="builder-profile-empty">
                    <span>No description yet. Click "Add" to tell Atlas about your business.</span>
                  </div>
                )}
              </div>

              {structured ? (
                <StructuredMemorySections
                  isCompanyScope={isCompanyScope}
                  profile={profileSummary}
                  memories={memories}
                  drafts={drafts}
                  onChangeDraft={handleChangeDraft}
                  onAddTyped={handleAddTypedMemory}
                  companyDraft={newMemory}
                  onChangeCompanyDraft={setNewMemory}
                  onAddCompany={handleAddMemory}
                  saving={isCompanyScope ? saving : memorySaving}
                  editing={editingMemory}
                  deletingId={deletingId}
                  onStartEdit={handleStartEdit}
                  onChangeEdit={handleChangeEdit}
                  onSaveEdit={isCompanyScope ? handleSaveMemoryEdit : handleSaveUserMemoryEdit}
                  onCancelEdit={() => setEditingMemory(null)}
                  onDelete={handleDeleteMemory}
                />
              ) : (
              /* Flat memories list (backend without memory types) */
              <div className="builder-profile-section">
                <div className="builder-profile-section-header">
                  <div className="builder-profile-section-label">
                    <IoBulbOutline size={16} />
                    <span>{isCompanyScope ? 'Company Memories' : 'User Memories'}</span>
                  </div>
                  {memories.length > 0 && (
                    <span className="builder-profile-memory-count">
                      {memories.length}
                    </span>
                  )}
                </div>
                <p className="builder-profile-section-hint">
                  {isCompanyScope
                    ? 'Atlas uses these memories when working in company mode.'
                    : 'Atlas automatically saves key details from your conversations to provide better, more personalized analysis over time.'}
                </p>

                {isCompanyScope && (
                  <div className="builder-profile-memory-compose">
                    <input
                      value={newMemory}
                      onChange={(event) => setNewMemory(event.target.value)}
                      placeholder="Add a company memory..."
                      maxLength={500}
                    />
                    <button
                      type="button"
                      className="builder-profile-btn builder-profile-btn-primary"
                      onClick={handleAddMemory}
                      disabled={saving || !newMemory.trim()}
                    >
                      Add
                    </button>
                  </div>
                )}

                {memories.length === 0 ? (
                  <div className="builder-profile-empty">
                    <IoBulbOutline size={20} className="builder-profile-empty-icon" />
                    <span>No memories yet.</span>
                    <span className="builder-profile-empty-sub">
                      As you chat with Atlas, it will remember important details about your
                      projects and preferences.
                    </span>
                  </div>
                ) : (
                  <div className="builder-profile-memories-list">
                    {memories.map((mem) => (
                      <div key={mem.id} className="builder-profile-memory-item">
                        <div className="builder-profile-memory-content">
                          {editingMemory?.id === mem.id ? (
                            <textarea
                              className="builder-profile-textarea builder-profile-memory-edit"
                              value={editingMemory.memory}
                              onChange={(event) => setEditingMemory((prev) => ({ ...prev, memory: event.target.value }))}
                              maxLength={500}
                            />
                          ) : (
                            <p className="builder-profile-memory-text">{mem.memory}</p>
                          )}
                          <span className="builder-profile-memory-date">
                            <IoTimeOutline size={12} />
                            {formatDate(mem.created_at)}
                          </span>
                        </div>
                        {isCompanyScope && editingMemory?.id === mem.id ? (
                          <div className="builder-profile-memory-actions">
                            <button type="button" className="builder-profile-edit-btn" onClick={handleSaveMemoryEdit} disabled={saving}>Save</button>
                            <button type="button" className="builder-profile-memory-delete" onClick={() => setEditingMemory(null)} aria-label="Cancel edit">
                              <IoCloseOutline size={14} />
                            </button>
                          </div>
                        ) : isCompanyScope ? (
                          <button
                            type="button"
                            className="builder-profile-memory-delete"
                            onClick={() => setEditingMemory({ id: mem.id, memory: mem.memory })}
                            title="Edit memory"
                            aria-label="Edit memory"
                          >
                            <IoCreateOutline size={14} />
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="builder-profile-memory-delete"
                          onClick={() => handleDeleteMemory(mem.id)}
                          disabled={deletingId === mem.id}
                          title="Remove memory"
                          aria-label="Remove memory"
                        >
                          {deletingId === mem.id ? (
                            <span className="builder-profile-spinner-sm" />
                          ) : (
                            <IoTrashOutline size={14} />
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
